import { useState, useEffect } from 'react';
import axios from 'axios';
import AIMakeoverModal from './AIMakeoverModal';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IdeasSection({ user, platforms }) {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingStates, setLoadingStates] = useState({}); // Track loading per idea and action
  
  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // AI Makeover modal state
  const [showMakeover, setShowMakeover] = useState(false);
  const [makeoverImage, setMakeoverImage] = useState(null);
  const [makeoverIdea, setMakeoverIdea] = useState(null);

  // Use current_industry for admin switcher, fallback to industry
  const effectiveIndustry = user.current_industry || user.industry;

  // Auto-generate ideas on mount or when industry changes
  useEffect(() => {
    if (ideas.length === 0) {
      generateIdeas();
    }
  }, [effectiveIndustry]);

  const generateIdeas = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: 'General',
        prompt: `Generate exactly 6 creative content ideas for ${effectiveIndustry} industry. Format EACH idea on a new line starting with the number (1., 2., 3., 4., 5., 6.) followed by a catchy title and brief description. Keep each idea as ONE paragraph.`
      });

      // Parse the ideas from the response
      const ideasText = response.data.content;
      
      // Split by numbers followed by period (1. 2. 3. etc.)
      const ideaSections = ideasText.split(/(?=\d+\.\s+)/g).filter(section => section.trim());
      
      const parsedIdeas = ideaSections.slice(0, 5).map((ideaText, index) => {
        // Remove the number prefix and clean up
        const cleanText = ideaText.replace(/^\d+\.\s*/, '').trim();
        
        // Try to split into title and description
        const lines = cleanText.split(/\n+/).filter(line => line.trim());
        const title = lines[0] || cleanText.substring(0, 50);
        const description = lines.slice(1).join(' ') || cleanText;
        
        return {
          id: index + 1,
          title: title.replace(/[*#]/g, '').trim(),
          description: description.replace(/[*#]/g, '').trim()
        };
      });

      setIdeas(parsedIdeas);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate ideas');
    } finally {
      setLoading(false);
    }
  };

  // Generate a single replacement idea
  const generateReplacementIdea = async (ideaId) => {
    try {
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: 'General',
        prompt: `Generate 1 creative content idea for ${effectiveIndustry} industry. Give just the title and brief description without numbering.`
      });

      const ideaText = response.data.content.trim();
      
      // Try to split into title and description
      const lines = ideaText.split(/\n+/).filter(line => line.trim());
      const title = lines[0] || ideaText.substring(0, 50);
      const description = lines.slice(1).join(' ') || ideaText;
      
      const newIdea = {
        id: ideaId,
        title: title.replace(/[*#]/g, '').trim(),
        description: description.replace(/[*#]/g, '').trim()
      };

      // Replace the used idea with the new one
      setIdeas(prevIdeas => 
        prevIdeas.map(idea => idea.id === ideaId ? newIdea : idea)
      );

      console.log('✅ Idea replaced successfully');

    } catch (err) {
      console.error('Failed to generate replacement idea:', err);
    }
  };

  const createAIContent = async (idea) => {
    const loadingKey = `content-${idea.id}`;
    try {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
      setError('');
      
      // Create AI content based on the idea
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: 'General',
        prompt: `Create a detailed social media post based on this idea: ${idea.title}. ${idea.description}`
      });

      // Save to posts with current industry
      const postResponse = await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: 'General',
        industry: effectiveIndustry
      });

      await axios.put(`${API}/posts/${postResponse.data.id}`, {
        content: response.data.content
      });

      alert('✅ AI Content created successfully!\n\nA fresh idea is being generated to replace this one.');
      
      // Generate replacement idea for the used one
      generateReplacementIdea(idea.id);

    } catch (err) {
      console.error('Content generation error:', err);
      setError(err.response?.data?.detail || 'Failed to create AI content');
      alert('❌ Failed to create AI content: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const createAIImage = async (idea) => {
    const loadingKey = `image-${idea.id}`;
    try {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
      setError('');
      
      console.log('Starting image generation for:', idea.title);
      console.log('Using industry:', effectiveIndustry);
      
      // Create AI typography-based image with the idea heading
      // AI generates the text as part of the image design (like Facts/Quotes)
      let imagePrompt = `Create a stunning typography-based social media post design with the text "${idea.title}" prominently displayed. Design style: Modern, professional, eye-catching with bold typography. ${idea.description}. Use elegant typography with creative layout, complementary background elements, professional color scheme. The text "${idea.title}" should be the main visual element - large, readable, beautifully designed with professional fonts. Make it look like a professional graphic designer created it for social media.`;
      
      if (user.brand_name) {
        imagePrompt += ` Include subtle brand element with text '${user.brand_name}' in a corner or as subtle branding.`;
      }
      imagePrompt += ` IMPORTANT: Keep the bottom right corner clear (no text or important elements there) for watermark placement.`;
      
      console.log('Sending image request with prompt:', imagePrompt);
      
      const response = await axios.post(`${API}/generate/image`, {
        user_id: user.id,
        industry: effectiveIndustry,
        prompt: imagePrompt
      });

      console.log('Image generated successfully:', response.data);

      // Store image for makeover and show preview with button
      setPreviewData({
        image: `data:image/png;base64,${response.data.image_base64}`,
        caption: `${idea.title}\n\n${idea.description}`,
        imageBase64: response.data.image_base64,
        idea: idea
      });
      setShowPreview(true);

    } catch (err) {
      console.error('Image generation error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to create AI image. Please try again.');
      alert('❌ Failed to create AI image: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const savePostToGallery = async () => {
    try {
      setLoading(true);
      
      // Save to posts with image and current industry
      const postResponse = await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: 'General',
        industry: effectiveIndustry
      });

      await axios.put(`${API}/posts/${postResponse.data.id}`, {
        content: previewData.caption,
        image_base64: previewData.imageBase64
      });

      console.log('Post saved successfully');

      alert('✅ Post created successfully!\n\nA fresh idea is being generated to replace this one.');
      
      // Generate replacement idea for the used one
      const usedIdeaId = previewData.idea.id;
      generateReplacementIdea(usedIdeaId);
      
      setShowPreview(false);
      setPreviewData(null);

    } catch (err) {
      console.error('Save post error:', err);
      alert('❌ Failed to save post: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Ideas Grid - 2 rows x 3 columns */}
      {ideas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ideas.map((idea) => (
            <div
              key={idea.id}
              className="theme-card border rounded-lg overflow-hidden hover:border-orange-500/50 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {/* Idea Header */}
              <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-b border-orange-500/20 p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0">
                    {idea.id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-bold accent-text mb-1 break-words line-clamp-2">{idea.title}</h4>
                    <p className="text-gray-300 text-xs leading-relaxed break-words line-clamp-3">
                      {idea.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Button - Only Image */}
              <div className="p-3 bg-gray-900/50">
                <button
                  onClick={() => createAIImage(idea)}
                  disabled={loadingStates[`image-${idea.id}`]}
                  className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loadingStates[`image-${idea.id}`] ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      <>🖼️ Generate Image</>
                    )}
                  </button>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Generate Button - Always Show */}
        {ideas.length > 0 && (
          <div className="theme-card border rounded-xl p-6">
            <p className="style={{ color: 'var(--text-muted)' }} mb-4">Want completely new ideas? Click below to regenerate all 5 ideas.</p>

            {/* Regenerate Button */}
            <button
              onClick={generateIdeas}
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed btn-glow"
            >
              {loading ? 'Generating New Ideas...' : '🔄 Regenerate All Ideas'}
            </button>

            {error && (
              <div className="mt-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && ideas.length === 0 && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold accent-text mb-2">Generating Ideas...</h3>
            <p className="style={{ color: 'var(--text-muted)' }}">Creating fresh content ideas for {effectiveIndustry} industry</p>
          </div>
        )}

      {/* Image Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-orange-500/50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border-b border-orange-500/30 px-6 py-4 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">🖼️ AI Image Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="style={{ color: 'var(--text-muted)' }} hover:text-white transition text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Generated Image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={previewData.image}
                  alt="Generated"
                  className="w-full h-auto"
                />
              </div>

              {/* Caption */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4">
                <label className="block text-sm font-semibold text-orange-400 mb-2">Caption:</label>
                <textarea
                  value={previewData.caption}
                  onChange={(e) => setPreviewData({ ...previewData, caption: e.target.value })}
                  className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg p-3 min-h-[120px] focus:outline-none focus:border-orange-500 transition"
                  placeholder="Edit caption..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setMakeoverImage(previewData.image);
                    setMakeoverIdea(previewData.idea);
                    setShowMakeover(true);
                    setShowPreview(false);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-bold hover:from-purple-600 hover:to-pink-600 transition shadow-lg"
                >
                  🎨 AI Makeover
                </button>
                <button
                  onClick={savePostToGallery}
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    '✨ Save Post'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Makeover Modal */}
      {showMakeover && (
        <AIMakeoverModal
          user={user}
          imageData={makeoverImage}
          onClose={() => setShowMakeover(false)}
          onSave={async (processedImage) => {
            try {
              // Save post with processed image
              await axios.post(`${API}/posts`, {
                user_id: user.id,
                industry: effectiveIndustry,
                platform: 'General',
                content: `${makeoverIdea.title}\n\n${makeoverIdea.description}`,
                image_base64: processedImage.split(',')[1]
              });
              
              alert('✅ Post saved successfully!');
              
              // Replace used idea with new one
              await replaceUsedIdea(makeoverIdea.id);
              
              setShowMakeover(false);
            } catch (error) {
              alert('Failed to save post: ' + (error.response?.data?.detail || error.message));
            }
          }}
        />
      )}
    </div>
  );
}

export default IdeasSection;
